// FRONT-2001: pure event router for the GPT-Live-1 data-channel protocol.
//
// A sibling to `voice-event-router.ts`, not an extension of it — the wire
// shapes are genuinely different protocols (OpenAI Realtime vs. OpenAI
// Live), and `voice-event-router.ts` stays untouched by this ticket. Same
// design as its sibling: a switch on the event `type` that produces a
// discriminated `LiveEventAction` the tracker/hook then applies, kept
// framework-free so it can be unit-tested under the plain-node vitest
// runner this repo's frontend tests run under.
//
// Every field read below is a MEASURED shape (final-plan.md §1, captured
// against the real API 2026-09-13) — see `live-call-tracker.test.ts` for
// real payload fixtures. Two shapes are worth calling out because they are
// NOT symmetric with the rest of the protocol:
//   - `session.commentary.appended` / `session.instructions.appended` /
//     `session.input_audio.muted|unmuted` all echo the client's own
//     `event_id` back at the TOP LEVEL, as `client_event_id`.
//   - `error` does NOT: its `client_event_id` (along with `code` and
//     `message`) is nested one level down, inside `error` itself. A real
//     captured payload: `{type:"error", event_id:"...",
//     error:{type:"invalid_request_error", code:"event_not_allowed",
//     message:"...", param:null, client_event_id:"steer_from_browser"}}`.

export type LiveEventAction =
  | { kind: 'started' }
  | { kind: 'input-delta'; delta: string; startMs: number; endMs: number }
  | { kind: 'output-delta'; delta: string; startMs: number; endMs: number }
  | { kind: 'delegation'; delegationId: string; offsetMs: number }
  | { kind: 'commentary-appended' }
  | { kind: 'instructions-appended' }
  | { kind: 'muted' }
  | { kind: 'unmuted' }
  | { kind: 'error'; code?: string; message: string; clientEventId?: string }
  | { kind: 'closed'; reason?: string }
  | { kind: 'ignore' };

interface LiveEvent {
  type?: string;
  delta?: string;
  start_ms?: number;
  end_ms?: number;
  offset_ms?: number;
  delegation?: { id?: string; type?: string; target?: string };
  reason?: string;
  error?: { code?: string; message?: string; client_event_id?: string; type?: string; param?: unknown };
}

export function routeLiveEvent(raw: unknown): LiveEventAction {
  if (!raw || typeof raw !== 'object') return { kind: 'ignore' };
  const e = raw as LiveEvent;
  const type = e.type ?? '';

  switch (type) {
    case 'session.started':
      return { kind: 'started' };

    case 'session.input_transcript.delta': {
      const delta = e.delta ?? '';
      if (!delta) return { kind: 'ignore' };
      return { kind: 'input-delta', delta, startMs: e.start_ms ?? 0, endMs: e.end_ms ?? 0 };
    }

    case 'session.output_transcript.delta': {
      const delta = e.delta ?? '';
      if (!delta) return { kind: 'ignore' };
      return { kind: 'output-delta', delta, startMs: e.start_ms ?? 0, endMs: e.end_ms ?? 0 };
    }

    case 'session.delegation.created': {
      const id = (e.delegation?.id ?? '').trim();
      if (!id) return { kind: 'ignore' };
      return { kind: 'delegation', delegationId: id, offsetMs: e.offset_ms ?? 0 };
    }

    case 'session.commentary.appended':
      return { kind: 'commentary-appended' };

    case 'session.instructions.appended':
      return { kind: 'instructions-appended' };

    case 'session.input_audio.muted':
      return { kind: 'muted' };

    case 'session.input_audio.unmuted':
      return { kind: 'unmuted' };

    case 'error': {
      const message = e.error?.message ?? 'Unknown live error';
      return { kind: 'error', code: e.error?.code, message, clientEventId: e.error?.client_event_id };
    }

    case 'session.closed':
      return { kind: 'closed', reason: e.reason };

    default:
      return { kind: 'ignore' };
  }
}
